import { FC } from "react";
import classNames from "classnames";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { TbCopy } from "react-icons/tb";
import { useToast } from "src/context/ToastContext";
import { STATUS_MAP } from "./constants";

type BookingHeroProps = {
  status: number;
  email?: string | null;
  referenceNumber?: string | null;
};

const BookingHero: FC<BookingHeroProps> = ({ status, email, referenceNumber }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const statusObject = STATUS_MAP?.[status as keyof typeof STATUS_MAP];
  const colorClasses = statusObject?.color;
  const descriptionKey = email ? statusObject?.description : (statusObject?.descriptionNoEmail ?? statusObject?.description);

  const handleCopy = () => {
    navigator.clipboard.writeText(referenceNumber ?? "");
    toast.success(t("UserHistory.giftcard.copiedToClipboard"));
  };

  return (
    <div
      className={`font-manrope text-font-default flex w-full flex-col items-center bg-gradient-to-b px-6 pt-12 pb-8 ${colorClasses?.gradient ?? ""}`}
    >
      <div className="flex flex-col items-center justify-center gap-3">
        <div className={classNames(colorClasses?.text, colorClasses?.background, "mb-1 rounded-full! p-2! outline")}>{statusObject?.icon}</div>
        <div className="text-center text-4xl font-bold">{statusObject && t(statusObject.title)}</div>
        {statusObject?.subtitle && (
          <div className={classNames(colorClasses?.text, "text-center text-lg font-bold")}>{t(statusObject.subtitle)}</div>
        )}
        <div className="text-font-subtle font-inter text-center text-sm leading-6">
          {descriptionKey ? (
            <Trans
              i18nKey={descriptionKey}
              values={{ email }}
              components={{ email: <span className="text-font-active font-semibold" dir="ltr" /> }}
            />
          ) : (
            statusObject && t(statusObject.text)
          )}
        </div>
        {statusObject?.showReference && referenceNumber && (
          <div className="bg-background-default border-border-subtle mt-1 flex items-center gap-3 rounded-pill border py-1.5 ps-4 pe-1.5 shadow-xs">
            <span className="text-font-subtle text-xs uppercase">{t("BookingResult.referenceNumber")}</span>
            <span className="text-font-active text-sm font-bold">{referenceNumber}</span>
            <button
              type="button"
              aria-label={t("BookingResult.copyReference")}
              onClick={handleCopy}
              className="bg-background-primary text-font-inverted flex size-7 cursor-pointer items-center justify-center rounded-full"
            >
              <TbCopy className="size-4" />
            </button>
          </div>
        )}
        {statusObject?.showBackToSearch && (
          <Link to="/hotel" className="bg-background-primary text-font-inverted font-inter mt-1 rounded-sm px-10 py-2.5 text-sm font-semibold">
            {t("BookingResult.backToSearch")}
          </Link>
        )}
      </div>
    </div>
  );
};

export default BookingHero;
